import type { CardAttachment } from "@shared/models/card-stack"
import ObjectId from "@shared/util/object-id-gen"
import type { Attribute } from "@tiptap/core"
import type { Node as PMNode } from "@tiptap/pm/model"
import type { EditorSurface } from "../../analytics/types"
import { renderStringAsData } from "../../util/render-data"

// The unified Link schema and its shared Tiptap attributes (docs/unified-links.md §3-4).
// Both node types — linkInline and linkBlock — point here; they differ only in
// inline/group and which subset of renderAs they allow.

export type AttachmentSource = "upload" | "google" | "dropbox" | "onedrive" | "other" | "link"

// Each node type allows only its own subset — an illegal pair like linkInline +
// "preview" must not compile, parse, or store (linkAttributes clamps it).
export type InlineRenderAs = "hyperlink" | "chip"
export type BlockRenderAs = "block" | "preview"
export type LinkRenderAs = InlineRenderAs | BlockRenderAs

/** linkInline's allowed renderAs values; the first is the default. */
export const INLINE_RENDER_AS: readonly InlineRenderAs[] = ["hyperlink", "chip"]
/** linkBlock's allowed renderAs values; the first is the default. */
export const BLOCK_RENDER_AS: readonly BlockRenderAs[] = ["block", "preview"]

/**
 * The one Link concept: a link is always a token (node) holding the whole schema,
 * whether it renders as a hyperlink, chip, block line, or preview.
 *
 * Extends the legacy CardAttachment shape with one rename: `id` → `attachmentId`, the
 * *attachment's* identity — shared by every token for that attachment and the key of
 * `attachmentSettings` (Google Classroom share-mode) — not a node id.
 */
export interface Link extends Omit<CardAttachment, "id"> {
  attachmentId: string
  /** Provenance hint (Google/Dropbox picker iconUrl; only those two set it). */
  icon: string | null
  /** Which path inserted the link (§6/§7). */
  source: AttachmentSource
  /** Which form to render; clamped per node type. */
  renderAs: LinkRenderAs
  // Cached page metadata; all null until unfurling is built (§8).
  previewTitle: string | null
  previewDescription: string | null
  previewImageUrl: string | null
  previewFaviconUrl: string | null
}

/**
 * Which opener started the link dialog. Each is a real surface in its own right, so the operations
 * a submit reports name the gesture the user made rather than the dialog they all share.
 */
export type LinkDialogOrigin = Extract<
  EditorSurface,
  "floating-toolbar" | "keyboard" | "link-popover"
>

/** Whether `node` is a unified link token (either form). */
export function isLinkNode(node: PMNode): boolean {
  return node.type.name === "linkInline" || node.type.name === "linkBlock"
}

/**
 * Shared addAttributes for both link node types: one plain-scalar `data-*` entry per
 * Link field, with `renderAs` clamped to the node type's `allowed` subset (its first
 * entry is the default) so an out-of-subset value can't be constructed, parsed, or
 * stored.
 */
export function linkAttributes(
  allowed: readonly LinkRenderAs[],
): Record<keyof Link, Partial<Attribute>> {
  const clampRenderAs = (value: unknown): LinkRenderAs =>
    allowed.includes(value as LinkRenderAs) ? (value as LinkRenderAs) : allowed[0]

  return {
    attachmentId: { ...renderStringAsData("attachmentId", "attachment-id"), default: null },
    url: { ...renderStringAsData("url"), isRequired: true },
    title: { ...renderStringAsData("title"), default: null },
    type: { ...renderStringAsData("type"), default: "link" },
    mimetype: { ...renderStringAsData("mimetype"), default: null },
    size: {
      default: null,
      renderHTML: (attributes) =>
        attributes.size === null ? {} : { "data-size": String(attributes.size) },
      parseHTML: (element) => {
        const raw = element.getAttribute("data-size")
        return raw === null ? null : Number(raw)
      },
    },
    isWriteable: {
      default: false,
      renderHTML: (attributes) => (attributes.isWriteable ? { "data-is-writeable": "" } : {}),
      parseHTML: (element) => element.hasAttribute("data-is-writeable"),
    },
    icon: { ...renderStringAsData("icon"), default: null },
    source: { ...renderStringAsData("source"), default: "link" },
    renderAs: {
      default: allowed[0],
      renderHTML: (attributes) => ({ "data-render-as": clampRenderAs(attributes.renderAs) }),
      parseHTML: (element) => clampRenderAs(element.getAttribute("data-render-as")),
    },
    previewTitle: { ...renderStringAsData("previewTitle", "preview-title"), default: null },
    previewDescription: {
      ...renderStringAsData("previewDescription", "preview-description"),
      default: null,
    },
    previewImageUrl: {
      ...renderStringAsData("previewImageUrl", "preview-image-url"),
      default: null,
    },
    previewFaviconUrl: {
      ...renderStringAsData("previewFaviconUrl", "preview-favicon-url"),
      default: null,
    },
  }
}

/**
 * Fresh Link attrs for an uploaded/picked file (docs/unified-links.md §6): a block
 * token (an attachment line), with the upload fields filled from the Filestack result
 * and the insert path's provenance persisted as `source` (§7). A new attachment
 * identity is minted.
 */
export function linkFromUpload(
  file: { url: string; filename: string; mimetype?: string; size?: number },
  source: AttachmentSource,
): Link {
  return {
    attachmentId: ObjectId.create(),
    url: file.url,
    title: file.filename,
    type: file.mimetype?.startsWith("image/") ? "image" : "file",
    mimetype: file.mimetype ?? null,
    size: file.size ?? null,
    isWriteable: false,
    icon: null,
    source,
    renderAs: "block",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  }
}

/** A file chosen from the Google Drive picker (its `docs` entries, normalized). */
export interface GoogleDrivePickedFile {
  /** The file's display name (`doc.name`). */
  title: string
  url: string
  /** The picker's iconUrl (`doc.iconUrl`), kept as the Link provenance hint. */
  icon: string | null
  /** "image" for a picked image, "file" for everything else. */
  type: "image" | "file"
}

/**
 * Fresh Link attrs for a Google Drive pick (docs/unified-links.md §6): a block token
 * whose fields come from the picker result, with "google" persisted as `source` (§7) and
 * the picker's icon kept as the provenance hint. A new attachment identity is minted.
 */
export function linkFromGooglePick(file: GoogleDrivePickedFile): Link {
  return {
    attachmentId: ObjectId.create(),
    url: file.url,
    title: file.title,
    type: file.type,
    mimetype: null,
    size: null,
    isWriteable: false,
    icon: file.icon,
    source: "google",
    renderAs: "block",
    previewTitle: null,
    previewDescription: null,
    previewImageUrl: null,
    previewFaviconUrl: null,
  }
}
